import { logger } from '../utils/logger';
import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Card, CardContent, Grid, Chip, Alert, CircularProgress, TextField, Snackbar
} from '@mui/material';
import {
  CloudUpload, Business, Store, FileUpload, Download
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import {
  handleQuickBooksImport as sharedHandleQuickBooksImport,
} from '../utils/quickbooksImport';
import { handleShopifyImport as sharedHandleShopifyImport } from '../utils/shopifyImport';
import { useAuth } from '../contexts/AuthContext';
import CompanySelector from './CompanySelector';
import { ImportDateRangeControls } from './ImportDateRangeControls';
import { companyService } from '../services/companyService';
import { API_URL } from '../services/api';
import { Company } from '../types/company';
import { useSharedImportDateRange } from '../hooks/useSharedImportDateRange';
import { sanitizeImportDateRange } from '../utils/importDateRange';

const SAMPLE_CSV_URL = '/sample-transactions.csv';

interface ImportTransactionsDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
  initialCompanyId?: number;
  onCompanyChange?: (companyId: number) => void;
  companies?: Company[];
  loadingCompanies?: boolean;
  companyError?: string | null;
  onRequestCompanyRefresh?: () => Promise<void> | void;
}

export const ImportTransactionsDialog: React.FC<ImportTransactionsDialogProps> = ({
  open,
  onClose,
  onComplete,
  initialCompanyId,
  onCompanyChange,
  companies: companiesProp,
  loadingCompanies: loadingCompaniesProp,
  companyError: companyErrorProp,
  onRequestCompanyRefresh,
}) => {
  const [selectedImportMethod, setSelectedImportMethod] = useState<string>('');
  const [importing, setImporting] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [shopifyStore, setShopifyStore] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean, message: string, severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [companyId, setCompanyId] = useState<number>(0);
  const [internalCompanies, setInternalCompanies] = useState<Company[]>([]);
  const [internalLoading, setInternalLoading] = useState<boolean>(false);
  const [internalError, setInternalError] = useState<string | null>(null);
  const {
    range: importDateRange,
    setRange: setImportDateRange,
    error: importDateRangeError
  } = useSharedImportDateRange();

  const navigate = useNavigate();
  const { user } = useAuth();

  const companies = companiesProp ?? internalCompanies;
  const companiesLoading = loadingCompaniesProp ?? internalLoading;
  const companyError = companyErrorProp ?? internalError;
  const hasMultipleCompanies = companies.length > 1;

  const fetchCompanies = useCallback(async () => {
    setInternalLoading(true);
    setInternalError(null);
    try {
      const fetchedCompanies = await companyService.listCompanies();
      setInternalCompanies(fetchedCompanies);
    } catch (err) {
      logger.error('[ImportDialog] Failed to load companies:', err);
      setInternalCompanies([]);
      setInternalError('Failed to load companies. Please try again.');
    } finally {
      setInternalLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (Array.isArray(companiesProp)) {
      setInternalCompanies(companiesProp);
      return;
    }
    fetchCompanies();
  }, [open, companiesProp, fetchCompanies]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (companyId) {
      return;
    }
    const list = companies;
    if (!list || list.length === 0) {
      return;
    }
    let resolved = 0;
    if (initialCompanyId && list.some((c) => c.id === initialCompanyId)) {
      resolved = initialCompanyId;
    } else if (user?.primary_company_id && list.some((c) => c.id === user.primary_company_id)) {
      resolved = user.primary_company_id;
    } else {
      resolved = list[0].id;
    }
    setCompanyId(resolved);
  }, [open, companies, companyId, initialCompanyId, user]);

  useEffect(() => {
    if (!open) {
      setCompanyId(0);
    }
  }, [open]);

  const handleCompanyRefresh = useCallback(async () => {
    if (onRequestCompanyRefresh) {
      await onRequestCompanyRefresh();
      return;
    }
    await fetchCompanies();
  }, [onRequestCompanyRefresh, fetchCompanies]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv'))) {
      setUploadedFile(file);
      setUploadError(null);
    } else {
      setUploadError('Please select a valid CSV file');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    multiple: false
  });

  const handleImportMethodSelect = (method: string) => {
    setSelectedImportMethod(method);
  };

  const parseUploadError = async (response: Response): Promise<string> => {
    const fallbackMessage = `Upload failed (HTTP ${response.status})`;
    try {
      const rawBody = await response.text();
      if (!rawBody) {
        return fallbackMessage;
      }

      try {
        const payload = JSON.parse(rawBody);
        return (
          payload?.detail ||
          payload?.message ||
          payload?.error ||
          fallbackMessage
        );
      } catch {
        const trimmed = rawBody.trim();
        if (!trimmed) {
          return fallbackMessage;
        }
        if (trimmed.startsWith('<!DOCTYPE')) {
          return `${fallbackMessage}. The server returned an HTML error page (likely from Cloudflare or the hosting proxy). Check the network tab for the status code details.`;
        }
        return trimmed.length > 600 ? `${trimmed.slice(0, 600)}…` : trimmed;
      }
    } catch (readErr) {
      logger.error('[ImportDialog] Unable to read upload error response body', readErr);
      return fallbackMessage;
    }
  };

  const handleCSVUpload = async () => {
    if (!uploadedFile) {
      setUploadError('Please select a CSV file');
      setSnackbar({ open: true, message: 'Please select a CSV file', severity: 'error' });
      return;
    }
    if (importDateRangeError) {
      setUploadError(importDateRangeError);
      setSnackbar({ open: true, message: importDateRangeError, severity: 'error' });
      return;
    }
    if (!companyId || companyId <= 0) {
      setUploadError('Please select a company before importing.');
      setSnackbar({ open: true, message: 'Please select a company before importing.', severity: 'error' });
      return;
    }
    setImporting(true);
    setUploadProgress(0);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('platform_name', 'CSV');
      formData.append('company_id', String(companyId));
      const { startDate, endDate } = sanitizeImportDateRange(importDateRange);
      formData.append('start_date', startDate);
      formData.append('end_date', endDate);
      const response = await fetch(`${API_URL}/api/v1/platforms/csv/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      setUploadProgress(100);
      if (!response.ok) {
        const errorMessage = await parseUploadError(response);
        logger.error('[ImportDialog] CSV upload failed', {
          status: response.status,
          statusText: response.statusText,
          errorMessage,
        });
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        throw new Error(errorMessage);
      }
      setImportComplete(true);
      setImporting(false);
      setSnackbar({ open: true, message: 'Successfully imported your transactions!', severity: 'success' });
      setTimeout(() => {
        setImportComplete(false);
        onComplete && onComplete();
        onClose();
      }, 1200);
    } catch (error: any) {
      setUploadError(error.message || 'Upload failed. Please try again.');
      setSnackbar({ open: true, message: error.message || 'Upload failed. Please try again.', severity: 'error' });
      setImporting(false);
      setUploadProgress(0);
    }
  };

  const handleImport = async () => {
    logger.log('[ImportDialog] handleImport called with method:', selectedImportMethod);
    if (selectedImportMethod === 'upload') {
      await handleCSVUpload();
    } else if (selectedImportMethod === 'quickbooks') {
      if (!user || !user.id || !user.email) {
        setUploadError('You must be logged in to connect QuickBooks.');
        setSnackbar({ open: true, message: 'You must be logged in to connect QuickBooks.', severity: 'error' });
        setImporting(false);
        return;
      }
      if (!companyId || companyId <= 0) {
        setUploadError('Please select a company before connecting QuickBooks.');
        setSnackbar({ open: true, message: 'Please select a company before connecting QuickBooks.', severity: 'error' });
        setImporting(false);
        return;
      }
      if (importDateRangeError) {
        setUploadError(importDateRangeError);
        setSnackbar({ open: true, message: importDateRangeError, severity: 'error' });
        setImporting(false);
        return;
      }
      await sharedHandleQuickBooksImport({
        source: 'import-dialog',
        user: {
          id: user.id,
          email: user.email,
          company_id: companyId || user.primary_company_id
        },
        setUploadError,
        setImporting,
        navigate,
        companyId: companyId || user.primary_company_id || undefined,
        dateRange: importDateRange
      });
      setSnackbar({ open: true, message: 'QuickBooks import started.', severity: 'success' });
      return;
    } else if (selectedImportMethod === 'shopify') {
      if (!user || !user.id || !user.email) {
        setUploadError('You must be logged in to connect Shopify.');
        setSnackbar({ open: true, message: 'You must be logged in to connect Shopify.', severity: 'error' });
        setImporting(false);
        return;
      }
      if (!shopifyStore) {
        setUploadError('Please enter your Shopify store name.');
        setSnackbar({ open: true, message: 'Please enter your Shopify store name.', severity: 'error' });
        setImporting(false);
        return;
      }
      if (!companyId || companyId <= 0) {
        setUploadError('Please select a company before connecting Shopify.');
        setSnackbar({ open: true, message: 'Please select a company before connecting Shopify.', severity: 'error' });
        setImporting(false);
        return;
      }
      if (importDateRangeError) {
        setUploadError(importDateRangeError);
        setSnackbar({ open: true, message: importDateRangeError, severity: 'error' });
        setImporting(false);
        return;
      }
      await sharedHandleShopifyImport({
        source: 'import-dialog',
        user: {
          id: user.id,
          email: user.email,
          company_id: companyId || user.primary_company_id
        },
        setUploadError,
        setImporting,
        navigate,
        companyId: companyId || user.primary_company_id || undefined,
        shop: shopifyStore,
        dateRange: importDateRange
      });
      setSnackbar({ open: true, message: 'Shopify import started.', severity: 'success' });
      return;
    } else {
      setImporting(true);
      await new Promise(resolve => setTimeout(resolve, 2000));
      setImportComplete(true);
      setImporting(false);
      setSnackbar({ open: true, message: 'Import complete.', severity: 'success' });
    }
  };

  const importOptions = [
    {
      title: 'Upload CSV File',
      description: 'Upload your sales transactions from a CSV file',
      icon: <CloudUpload sx={{ fontSize: 40 }} />, 
      action: 'upload',
      features: ['Drag & drop support', 'Automatic column mapping', 'Bulk import'],
    },
    {
      title: 'Connect QuickBooks',
      description: 'Sync your transactions directly from QuickBooks',
      icon: <Business sx={{ fontSize: 40 }} />, 
      action: 'quickbooks',
      features: ['Real-time sync', 'Automatic updates', 'Secure connection'],
    },
    {
      title: 'Connect Shopify',
      description: 'Import your Shopify store transactions',
      icon: <Store sx={{ fontSize: 40 }} />, 
      action: 'shopify',
      features: ['Live data sync', 'Order tracking', 'Tax calculation'],
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Transactions</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Import for Company
            </Typography>
            <CompanySelector
              selectedCompanyId={companyId}
              onCompanyChange={(cid) => {
                setCompanyId(cid);
                if (onCompanyChange) {
                  onCompanyChange(cid);
                }
              }}
              disabled={companiesLoading || (!hasMultipleCompanies && companies.length <= 1)}
              includeAllOption={false}
              companies={companies}
              loading={companiesLoading}
              error={companyError}
              onRefresh={handleCompanyRefresh}
            />
          </Box>
          <Typography variant="h6" gutterBottom>
            Choose Your Import Method
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Select the best way to import your sales transactions:
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {importOptions.map((option) => (
              <Grid item xs={12} md={4} key={option.action}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: selectedImportMethod === option.action ? 2 : 1,
                    borderColor: selectedImportMethod === option.action ? 'primary.main' : 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                  }}
                  onClick={() => handleImportMethodSelect(option.action)}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box sx={{ color: 'primary.main', mb: 2 }}>
                      {option.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {option.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {option.description}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      {option.features.map((feature, index) => (
                        <Chip
                          key={index}
                          label={feature}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {selectedImportMethod && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Selected: <strong>{importOptions.find(opt => opt.action === selectedImportMethod)?.title}</strong>
              </Typography>
            </Box>
          )}
          {selectedImportMethod === 'upload' && (
            <Box sx={{ mt: 3 }}>
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'grey.300',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: isDragActive ? 'primary.light' : 'background.paper',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'primary.light',
                  },
                }}
              >
                <input {...getInputProps()} />
                <FileUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                {uploadedFile ? (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      File Selected: {uploadedFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Size: {(uploadedFile.size / 1024).toFixed(1)} KB
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {isDragActive ? 'Drop the CSV file here' : 'Drag & drop a CSV file here'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      or click to browse files
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  startIcon={<Download />}
                  component="a"
                  href={SAMPLE_CSV_URL}
                  download
                  sx={{ mt: 1 }}
                >
                  Download Sample CSV
                </Button>
              </Box>
              <Box sx={{ mt: 3 }}>
                <ImportDateRangeControls
                  range={importDateRange}
                  onChange={setImportDateRange}
                  helperText="Only transactions within this range will be processed after upload."
                  errorText={importDateRangeError}
                />
              </Box>
              {importing && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      Uploading... {uploadProgress}%
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1 }}>
                    <Box
                      sx={{
                        width: `${uploadProgress}%`,
                        height: 8,
                        bgcolor: 'primary.main',
                        borderRadius: 1,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>
                </Box>
              )}
              {uploadError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {uploadError}
                </Alert>
              )}
            </Box>
          )}
          {selectedImportMethod === 'quickbooks' && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary" paragraph>
                We'll connect to your QuickBooks account and import your transactions automatically.
              </Typography>
              <ImportDateRangeControls
                range={importDateRange}
                onChange={setImportDateRange}
                helperText="Leave the dates as-is to fetch the last 365 days."
                errorText={importDateRangeError}
              />
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Using your existing authentication to connect securely to QuickBooks.
                </Typography>
              </Alert>
            </Box>
          )}
          {selectedImportMethod === 'shopify' && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary" paragraph>
                We'll connect to your Shopify store and import all your order data automatically.
              </Typography>
              <ImportDateRangeControls
                range={importDateRange}
                onChange={setImportDateRange}
                helperText="Leave the dates as-is to fetch the last 365 days."
                errorText={importDateRangeError}
              />
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Using your existing authentication to connect securely to Shopify.
                </Typography>
              </Alert>
              <TextField
                label="Shopify Store Name"
                value={shopifyStore}
                onChange={e => setShopifyStore(e.target.value)}
                required
                fullWidth
                margin="normal"
                placeholder="yourstore (for yourstore.myshopify.com)"
              />
            </Box>
          )}
          {importing ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body1">
                {selectedImportMethod === 'upload' && 'Uploading your transactions...'}
                {selectedImportMethod === 'quickbooks' && 'Connecting to QuickBooks and importing data...'}
                {selectedImportMethod === 'shopify' && 'Connecting to Shopify and importing data...'}
              </Typography>
            </Box>
          ) : importComplete ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body1">
                ✅ Successfully imported your transactions!
              </Typography>
            </Alert>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          color="primary"
          disabled={importing || !selectedImportMethod || (selectedImportMethod === 'upload' && !uploadedFile) || (selectedImportMethod === 'shopify' && !shopifyStore)}
        >
          Import
        </Button>
      </DialogActions>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          style: { backgroundColor: snackbar.severity === 'success' ? '#43a047' : '#d32f2f', color: '#fff' },
        }}
      />
    </Dialog>
  );
};

